import { useEffect, useMemo, useRef } from 'react'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { useLocation } from 'react-router-dom'

import { useUserStore } from '@/stores/user.store'
import { useOnboardingStore } from '@/stores/onboarding.store'

type WizardStep = {
  element: string
  popover: {
    title?: string
    description: string
    side?: string
    align?: string
  }
}

/** ✅ keep these OUTSIDE the component */
const settingsSteps: WizardStep[] = [
  {
    element: '#settings-root',
    popover: {
      title: 'Willkommen in den Einstellungen',
      description:
        'Hier können Sie Ihre Daten für Rechnungen, Bestellungen, Auftraggeber, Nutzerdaten und Vertragsdaten eintragen und pflegen. Außerdem können Sie hier Ihre Benachrichtigungen an oder ausschalten',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '#settings-tab-invoice',
    popover: {
      title: 'Einstellungen konfigurieren',
      description:
        'Pflegen Sie hier alle zentralen Daten, die für eine reibungslose Nutzung erforderlich sind.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '#settings-tab-invoice',
    popover: {
      title: 'Rechnungen',
      description:
        'Hinterlegen Sie alle erforderlichen Pflichtangaben, damit Ihre Rechnungen korrekt erstellt werden.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '#settings-tab-orders',
    popover: {
      title: 'Bestellungen',
      description:
        'Pflegen Sie die vereinbarten Kapazitäten, die mit Union Investment abgestimmt wurden.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '#settings-tab-bank',
    popover: {
      title: 'Auftraggeber',
      description:
        'Verwalten Sie externe Auftraggeber und hinterlegen Sie die Preise Ihrer Leistungen. Union Investment ist bereits fest hinterlegt.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '#settings-tab-profile',
    popover: {
      title: 'Nutzerdaten',
      description:
        'Aktualisieren Sie Ihre persönlichen Angaben, die für die Rechnungsstellung verwendet werden.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '#settings-tab-contract',
    popover: {
      title: 'Vertragsdaten',
      description:
        'Behalten Sie Ihre verbleibende Vertragslaufzeit im Blick und kündigen Sie den Vertrag bei Bedarf.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '#settings-tab-notifications',
    popover: {
      title: 'Benachrichtigungen',
      description: 'Legen Sie fest, welche Benachrichtigungen Sie erhalten möchten.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '#settings-restart-onboarding',
    popover: {
      title: 'Einführung erneut starten',
      description:
        'Mit dieser Schaltfläche können Sie Ihren Onboarding-Prozess jederzeit neu starten.',
      side: 'top',
      align: 'center',
    },
  },
]

const dashboardSteps: WizardStep[] = [
  {
    element: '#dashboard-root',
    popover: {
      title: 'Willkommen im Dashboard',
      description:
        'Willkommen bei SIB-Software. Hier können Sie auf Bestellungen, Aktivitäten und Einstellungen zugreifen. Mit dieser Schaltfläche können Sie Ihren Onboarding-Prozess neu starten.',
      side: 'top',
      align: 'center',
    },
  },
  {
    element: '#new-event-button',
    popover: {
      title: 'Neues Ereignis',
      description:
        'Sie können im gesamten SIB Portal per Klick auf ‚Ereignis hinzufügen‘ eine neue Aktivität oder Bestellung erstellen oder weitere Aktionen ausführen.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '#dashboard-widget-config',
    popover: {
      title: 'Dashboard konfigurieren',
      description: 'Hier können Sie festlegen, welche Dashboard-Elemente angezeigt werden.',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '#orders-widget',
    popover: {
      title: 'Bestellungen',
      description:
        'Im Bereich „Bestellungen“ erstellen und verfolgen Sie Ihre Anfragen an Union Investment.',
      side: 'top',
      align: 'center',
    },
  },
  // {
  //   element: '#new-order-button',
  //   popover: {
  //     title: 'Neue Bestellung',
  //     description:
  //       'Über diesen Button legen Sie eine neue Bestellung an. Sie wählen Kunde, Zeitraum und Leistungsmengen.',
  //     side: 'bottom',
  //     align: 'center',
  //   },
  // },
  {
    element: '#activities-widget',
    popover: {
      title: 'Aktivitäten',
      description:
        'Hier erfassen Sie Ihre durchgeführten Tätigkeiten. Diese bilden die Basis der monatlichen Abrechnung.',
      side: 'top',
      align: 'center',
    },
  },
  // {
  //   element: '#new-activity-button',
  //   popover: {
  //     title: 'Aktivität hinzufügen',
  //     description: 'Mit diesem Button dokumentieren Sie eine neue Aktivität.',
  //     side: 'bottom',
  //     align: 'center',
  //   },
  // },
  {
    element: '#settings-navigation',
    popover: {
      title: 'Einstellungen',
      description:
        'In den Einstellungen pflegen Sie persönliche Daten, Bankdaten und Steuereinstellungen.',
      side: 'right',
      align: 'start',
    },
  },
]
type OnboardingArea = 'dashboard' | 'settings'

export function OnboardingWizard() {
  const location = useLocation()
  const { user } = useUserStore()

  const {
    isActive,
    completed,
    currentStep,
    shouldStartSettingsAfterDashboard,
    startWizard,
    stopWizard,
    completeWizard,
    setStep,
  } = useOnboardingStore()

  const driverRef = useRef<any>(null)

  const currentStepRef = useRef<number>(currentStep)
  useEffect(() => {
    currentStepRef.current = currentStep
  }, [currentStep])

  const isOnDashboard = location.pathname.startsWith('/app/user/dashboard')
  const isOnSettings = location.pathname.startsWith('/settings')

  const area: OnboardingArea | null = isOnSettings
    ? 'settings'
    : isOnDashboard
      ? 'dashboard'
      : null

  const activeSteps = useMemo(() => {
    if (area === 'settings') return settingsSteps
    if (area === 'dashboard') return dashboardSteps
    return null
  }, [area])

  /**
   * ✅ Auto-start rules:
   * - Start dashboard onboarding on first login if not completed.dashboard
   * - After dashboard is completed, when user goes to settings, start settings onboarding once
   * - Also start settings onboarding on first login IF user lands there first
   */
  useEffect(() => {
    if (!user) return
    if (!area) return
    if (isActive) return

    const firstLoginBackend = (user as any)?.firstLogin === true
    const firstLoginLocal = !localStorage.getItem('sib-onboarding-seen')
    const isFirstLogin = firstLoginBackend || firstLoginLocal

    // mark "seen" once we start anything
    const startAndMarkSeen = () => {
      startWizard()
      localStorage.setItem('sib-onboarding-seen', '1')
    }

    // 1) normal first login auto-start for current area
    if (isFirstLogin && completed[area] === false) {
      startAndMarkSeen()
      return
    }

    // 2) special flow: finished dashboard -> now entered settings -> start settings wizard once
    if (area === 'settings' && completed.settings === false && shouldStartSettingsAfterDashboard) {
      startAndMarkSeen()
      return
    }
  }, [user, area, isActive, completed, shouldStartSettingsAfterDashboard, startWizard])

  /**
   * Driver lifecycle (same as your working version),
   * but make sure completion is per-area:
   */
  useEffect(() => {
    if (!isActive) {
      if (driverRef.current?.isActive?.()) driverRef.current.destroy()
      driverRef.current = null
      return
    }

    if (!activeSteps || !area) {
      if (driverRef.current?.isActive?.()) driverRef.current.destroy()
      driverRef.current = null
      stopWizard()
      return
    }

    if (driverRef.current?.isActive?.()) return

    let cancelled = false

    const raf = requestAnimationFrame(() => {
      if (cancelled) return

      const resolvedSteps = activeSteps.filter(s => document.querySelector(s.element))
      if (resolvedSteps.length === 0) {
        stopWizard()
        return
      }

      const driverObj = driver({
        steps: resolvedSteps,
        showProgress: true,
        animate: true,
        disableActiveInteraction: true,
        allowClose: true,
        overlayOpacity: 0.6,
        stagePadding: 8,
        showButtons: ['next', 'previous', 'close'],
        nextBtnText: 'Weiter',
        prevBtnText: 'Zurück',
        doneBtnText: 'Fertig',
        progressText: '{{current}} von {{total}}',

        onHighlighted: (_el: any, _step: any, ctx: any) => {
          const idx = ctx?.state?.activeIndex ?? 0
          setStep(idx)
        },

        // ✅ Robust "Done" detection
        onNextClick: (_el: any, _step: any, ctx: any) => {
          const idx = ctx?.state?.activeIndex ?? 0
          const last = resolvedSteps.length - 1

          if (idx >= last) {
            completeWizard(area) // ✅ marks completed.dashboard/settings
            driverObj.destroy() // triggers cleanup
            return
          }

          setStep(idx + 1)
          driverObj.moveNext()
        },

        onPrevClick: (_el: any, _step: any, ctx: any) => {
          const idx = ctx?.state?.activeIndex ?? 0
          setStep(Math.max(0, idx - 1))
          driverObj.movePrevious()
        },

        onCloseClick: () => {
          stopWizard()
          driverObj.destroy()
        },

        onDestroyed: () => {
          driverRef.current = null
        },
      })

      driverRef.current = driverObj

      const step = currentStepRef.current
      if (step > 0 && step < resolvedSteps.length) driverObj.drive(step)
      else driverObj.drive()
    })

    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      if (driverRef.current?.isActive?.()) driverRef.current.destroy()
      driverRef.current = null
    }
  }, [isActive, activeSteps, area, location.pathname, setStep, completeWizard, stopWizard])

  return null
}
